import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useStore, formatApiError } from "../context/StoreContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Eye, EyeOff, Phone, Mail, Gift, ShieldCheck, Truck, Heart, ArrowRight, ArrowLeft } from "lucide-react";
import OtpInput from "../components/OtpInput";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { EASE } from "../components/Motion";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^\d{10}$/;
const pwOk = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

const input = "w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-3.5 text-sm text-[#2E1A22] outline-none focus:border-[#7E1E3F] focus:shadow-[0_0_0_3px_rgba(126,30,63,0.1)] transition-[border-color,box-shadow] placeholder:text-[#857078]/50";

function GoogleButton({ onDone }) {
  const [clientId, setClientId] = useState(undefined);
  useEffect(() => {
    api.get("/auth/config").then((r) => setClientId(r.data.google_client_id || null)).catch(() => setClientId(null));
  }, []);

  // Savyron-branded Google sign-in via Savyron's own OAuth client (active once GOOGLE_CLIENT_ID is configured)
  if (clientId) {
    return (
      <div className="flex justify-center" data-testid="google-signin-button">
        <GoogleOAuthProvider clientId={clientId}>
          <GoogleLogin
            theme="outline" size="large" shape="rectangular" text="continue_with" width="320"
            onSuccess={async (resp) => {
              try {
                const { data } = await api.post("/auth/google/token", { credential: resp.credential });
                localStorage.setItem("savyron_token", data.token);
                onDone(data.user);
              } catch (err) { toast.error(formatApiError(err)); }
            }}
            onError={() => toast.error("Google sign-in failed. Please try again.")}
          />
        </GoogleOAuthProvider>
      </div>
    );
  }
  if (clientId === undefined) return null;

  // Fallback: platform-managed Google sign-in until Savyron's own OAuth client is configured
  return (
    <button type="button" onClick={() => {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/account';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }}
      className="w-full flex items-center justify-center gap-3 border border-[#E5E7EB] bg-white text-[#2E1A22] text-sm font-medium rounded-xl py-3.5 hover:border-[#7E1E3F]/50 hover:shadow-[0_8px_20px_rgba(46,26,34,0.08)] transition-[border-color,box-shadow] duration-300"
      data-testid="google-signin-button">
      <svg width="17" height="17" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z" /><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" /><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" /><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" /></svg>
      Continue with Google
    </button>
  );
}

function Field({ label, error, valid, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-xs font-mono-lux tracking-[0.12em] uppercase text-[#857078]">{label}</span>
      <div className="relative mt-1.5">
        {children}
        {(valid || error) && (
          <span className={`absolute right-3.5 top-1/2 -translate-y-1/2 ${valid ? "text-[#2A9D8F]" : "text-[#E63946]"}`}>
            {valid ? <Check size={16} /> : <X size={16} />}
          </span>
        )}
      </div>
      {error && <p className="text-xs text-[#E63946] mt-1.5" data-testid={`${testid}-error`}>❌ {error}</p>}
      {valid && !error && <p className="text-xs text-[#2A9D8F] mt-1.5" data-testid={`${testid}-valid`}>✓ Looks good</p>}
    </label>
  );
}

function ResendButton({ onResend, testid, startAt = 0 }) {
  const [cooldown, setCooldown] = useState(startAt);
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);
  const click = async () => {
    try {
      await onResend();
      setCooldown(60);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  return (
    <button type="button" onClick={click} disabled={cooldown > 0}
      className="text-xs text-[#7E1E3F] hover:text-[#5C1230] disabled:text-[#857078] transition-colors"
      data-testid={testid}>
      {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend OTP"}
    </button>
  );
}

function BrandPanel() {
  return (
    <div className="hidden lg:flex flex-col justify-center flex-1 pr-14">
      <img src="/assets/savyron-logo.png" alt="SAVYRON" className="h-24 w-auto self-start" />
      <h1 className="font-serif-lux text-5xl text-[#2E1A22] mt-10 leading-tight">
        Thoughtful Gifts<br /><span className="gold-text italic">for Every Moment</span>
      </h1>
      <div className="grid grid-cols-2 gap-6 mt-12 max-w-md">
        {[[Gift, "Wide Gift Collection"], [ShieldCheck, "Secure Shopping"], [Truck, "Fast & Reliable Delivery"], [Heart, "Perfect for Every Occasion"]].map(([Icon, t]) => (
          <div key={t} className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-full bg-[#7E1E3F]/8 border border-[#7E1E3F]/15 flex items-center justify-center shrink-0">
              <Icon size={18} className="text-[#7E1E3F]" />
            </span>
            <p className="text-xs text-[#45303A] leading-snug">{t}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function OtpStep({ email, phone, channel, purpose, onVerified, onBack, onSwitchToEmail }) {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const isSms = channel === "sms";
  const target = isSms ? `+91 ${phone}` : email;

  const verify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = isSms
        ? await api.post("/auth/sms-otp/verify", { phone, otp, purpose })
        : await api.post("/auth/otp/verify", { email, otp, purpose });
      onVerified(data);
    } catch (err) {
      setError(formatApiError(err));
      setOtp("");
    }
    setLoading(false);
  };

  return (
    <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, ease: EASE }}>
      <h2 className="font-serif-lux text-3xl text-[#2E1A22]">{isSms ? "Check Your Phone" : "Check Your Email"}</h2>
      <p className="text-sm text-[#857078] mt-2">We sent a 6-digit verification code to <span className="text-[#2E1A22] font-medium" data-testid="otp-target">{target}</span>{isSms ? " via SMS" : ""}. It expires in 10 minutes.</p>
      <form onSubmit={verify} className="mt-8 space-y-6">
        <OtpInput value={otp} onChange={setOtp} />
        {error && <p className="text-sm text-[#E63946] text-center" data-testid="otp-error">❌ {error}</p>}
        <button disabled={otp.length !== 6 || loading}
          className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="otp-verify-button">
          {loading ? "Verifying…" : <>Verify & Continue <ArrowRight size={15} /></>}
        </button>
        <div className="flex justify-between items-center text-xs">
          <button type="button" onClick={onBack} className="text-[#857078] hover:text-[#7E1E3F] flex items-center gap-1" data-testid="otp-back"><ArrowLeft size={12} /> Back</button>
          <ResendButton testid="otp-resend" startAt={60} onResend={async () => {
            if (isSms) await api.post("/auth/sms-otp/send", { phone, purpose });
            else await api.post("/auth/otp/send", { email, purpose });
            toast.success("A new code is on its way");
          }} />
        </div>
        {isSms && onSwitchToEmail && (
          <button type="button" onClick={onSwitchToEmail}
            className="w-full text-center text-xs text-[#7E1E3F] hover:underline"
            data-testid="otp-switch-email">
            Didn't get the SMS? Send code to my email instead
          </button>
        )}
      </form>
    </motion.div>
  );
}

export default function Auth({ mode = "login" }) {
  const { login, setUser } = useStore();
  const navigate = useNavigate();
  const [step, setStep] = useState("identify");
  const [tab, setTab] = useState("phone");
  const [form, setForm] = useState({ name: "", phone: "", email: "", password: "", confirm: "" });
  const [touched, setTouched] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [channel, setChannel] = useState("email");

  const set = (k, v) => { setForm((p) => ({ ...p, [k]: v })); setError(""); };
  const blur = (k) => setTouched((p) => ({ ...p, [k]: true }));
  const show = (k) => touched[k];

  const done = (user) => {
    setUser(user);
    toast.success("Welcome to Savyron");
    navigate("/account");
  };

  // ---------- LOGIN ----------
  const identify = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setLoading(true);
    try {
      if (tab === "phone") {
        if (!PHONE_RE.test(form.phone)) { setError("Please enter a valid 10-digit mobile number"); setLoading(false); return; }
        const { data } = await api.post("/auth/lookup", { phone: form.phone });
        if (!data.exists) {
          setNotice("No account found with this number. Create one — it takes 30 seconds.");
        } else {
          try {
            await api.post("/auth/sms-otp/send", { phone: form.phone, purpose: "login" });
            setChannel("sms");
            setStep("otp");
            toast.success(`OTP sent to +91 ${form.phone}`);
          } catch (err) {
            if (err?.response?.status === 503) setNotice("SMS sign-in isn't available yet. Please continue with your email instead.");
            else setError(formatApiError(err));
          }
        }
      } else {
        if (!EMAIL_RE.test(form.email)) { setError("Please enter a valid email address"); setLoading(false); return; }
        const { data } = await api.post("/auth/lookup", { email: form.email });
        if (data.exists) setStep("password");
        else setNotice("No account found with this email. Create one below.");
      }
    } catch (err) { setError(formatApiError(err)); }
    setLoading(false);
  };

  const passwordLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const user = await login(form.email, form.password);
      done(user);
    } catch (err) { setError(formatApiError(err)); }
    setLoading(false);
  };

  const sendLoginOtp = async () => {
    setError(""); setLoading(true);
    try {
      await api.post("/auth/otp/send", { email: form.email, purpose: "login" });
      setChannel("email");
      setStep("otp");
      toast.success("Verification code sent to your email");
    } catch (err) { setError(formatApiError(err)); }
    setLoading(false);
  };

  // ---------- SIGNUP ----------
  const signup = async (e) => {
    e.preventDefault();
    setTouched({ name: true, phone: true, email: true, password: true, confirm: true });
    setError("");
    if (form.name.trim().length < 2) return setError("Please enter your full name");
    if (!PHONE_RE.test(form.phone)) return setError("Please enter a valid 10-digit mobile number");
    if (!EMAIL_RE.test(form.email)) return setError("Please enter a valid email address");
    if (!pwOk(form.password)) return setError("Password must be at least 8 characters with a letter and a number");
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setLoading(true);
    const payload = { name: form.name, phone: form.phone, email: form.email, password: form.password };
    try {
      await api.post("/auth/signup/init", { ...payload, channel: "sms" });
      setChannel("sms");
      setStep("otp");
      toast.success(`OTP sent to +91 ${form.phone}`);
    } catch (err) {
      if (err?.response?.status === 503) {
        try {
          await api.post("/auth/signup/init", { ...payload, channel: "email" });
          setChannel("email");
          setStep("otp");
          toast.success("SMS unavailable — verification code sent to your email");
        } catch (err2) { setError(formatApiError(err2)); }
      } else setError(formatApiError(err));
    }
    setLoading(false);
  };

  // ---------- FORGOT ----------
  const forgotSend = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(form.email)) return setError("Please enter a valid email address");
    setLoading(true);
    try {
      await api.post("/auth/otp/send", { email: form.email, purpose: "reset" });
      setChannel("email");
      setStep("otp");
      toast.success("Verification code sent");
    } catch (err) { setError(formatApiError(err)); }
    setLoading(false);
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (!pwOk(form.password)) return setError("Password must be at least 8 characters with a letter and a number");
    if (form.password !== form.confirm) return setError("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token: resetToken, password: form.password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) { setError(formatApiError(err)); }
    setLoading(false);
  };

  const onOtpVerified = (data) => {
    if (mode === "forgot") {
      setResetToken(data.reset_token);
      setStep("newpass");
    } else {
      localStorage.setItem("savyron_token", data.token);
      done(data.user);
    }
  };

  const isLogin = mode === "login";
  const isSignup = mode === "register";
  const isForgot = mode === "forgot";

  return (
    <div className="min-h-screen bg-[#FBF7F1] flex items-center" data-testid="auth-page">
      <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 py-28 flex items-center gap-10">
        <BrandPanel />
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
          className="w-full max-w-md mx-auto bg-white rounded-3xl border border-[#E5E7EB] shadow-[0_20px_60px_rgba(46,26,34,0.08)] p-7 sm:p-9">
          <div className="flex justify-between items-center mb-7">
            <img src="/assets/savyron-logo.png" alt="SAVYRON" className="h-10 w-auto lg:hidden" />
            <span className="hidden lg:block" />
            {isLogin ? (
              <p className="text-xs text-[#857078]">New to Savyron? <Link to="/register" className="text-[#7E1E3F] font-medium underline" data-testid="auth-switch-register">Create Account</Link></p>
            ) : (
              <p className="text-xs text-[#857078]">Have an account? <Link to="/login" className="text-[#7E1E3F] font-medium underline" data-testid="auth-switch-login">Sign In</Link></p>
            )}
          </div>

          <AnimatePresence mode="wait">
            {/* ---------- LOGIN: IDENTIFY ---------- */}
            {isLogin && step === "identify" && (
              <motion.div key="identify" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-serif-lux text-3xl text-[#2E1A22]">Welcome Back</h2>
                <p className="text-sm text-[#857078] mt-1.5">Login to continue to Savyron</p>
                <div className="flex bg-[#FBF7F1] rounded-xl p-1 mt-6 mb-6" data-testid="login-method-toggle">
                  {[["phone", Phone, "Phone Number"], ["email", Mail, "Email"]].map(([k, Icon, label]) => (
                    <button key={k} type="button" onClick={() => { setTab(k); setError(""); setNotice(""); }}
                      className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm transition-colors ${tab === k ? "bg-[#7E1E3F] text-white" : "text-[#857078] hover:text-[#2E1A22]"}`}
                      data-testid={`login-tab-${k}`}>
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
                <form onSubmit={identify} className="space-y-5">
                  {tab === "phone" ? (
                    <Field label="Phone Number" testid="login-phone"
                      valid={PHONE_RE.test(form.phone)} error={show("phone") && form.phone && !PHONE_RE.test(form.phone) ? "Please enter a valid 10-digit mobile number" : ""}>
                      <div className="flex">
                        <span className="bg-[#FBF7F1] border border-r-0 border-[#E5E7EB] rounded-l-xl px-3.5 py-3.5 text-sm text-[#857078] font-medium">+91</span>
                        <input className={`${input} rounded-l-none pr-10`} inputMode="numeric" maxLength={10} placeholder="98765 43210"
                          value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} onBlur={() => blur("phone")} data-testid="login-phone-input" />
                      </div>
                    </Field>
                  ) : (
                    <Field label="Email Address" testid="login-email"
                      valid={EMAIL_RE.test(form.email)} error={show("email") && form.email && !EMAIL_RE.test(form.email) ? "Please enter a valid email address" : ""}>
                      <input type="email" className={`${input} pr-10`} placeholder="name@gmail.com"
                        value={form.email} onChange={(e) => set("email", e.target.value)} onBlur={() => blur("email")} data-testid="login-email-input" />
                    </Field>
                  )}
                  {error && <p className="text-sm text-[#E63946]" data-testid="auth-error">❌ {error}</p>}
                  {notice && (
                    <div className="text-sm text-[#7E1E3F] bg-[#7E1E3F]/6 border border-[#7E1E3F]/15 rounded-xl px-4 py-3" data-testid="auth-notice">
                      {notice} {notice.includes("Create one") && <Link to="/register" className="underline font-medium">Create Account</Link>}
                    </div>
                  )}
                  <button disabled={loading}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                    data-testid="login-continue-button">
                    {loading ? "Please wait…" : <>Continue <ArrowRight size={15} /></>}
                  </button>
                </form>
              </motion.div>
            )}

            {/* ---------- LOGIN: PASSWORD ---------- */}
            {isLogin && step === "password" && (
              <motion.div key="password" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-serif-lux text-3xl text-[#2E1A22]">Welcome Back</h2>
                <p className="text-sm text-[#857078] mt-1.5">Signing in as <span className="text-[#2E1A22] font-medium">{form.email}</span></p>
                <form onSubmit={passwordLogin} className="mt-7 space-y-5">
                  <Field label="Password" testid="login-password">
                    <input type={showPw ? "text" : "password"} className={`${input} pr-10`} placeholder="Your password"
                      value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="auth-password" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#857078]" data-testid="toggle-password">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </Field>
                  {error && <p className="text-sm text-[#E63946]" data-testid="auth-error">❌ {error}</p>}
                  <button disabled={loading}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
                    data-testid="auth-submit">
                    {loading ? "Signing in…" : "Sign In"}
                  </button>
                  <div className="flex justify-between text-xs">
                    <button type="button" onClick={() => setStep("identify")} className="text-[#857078] hover:text-[#7E1E3F] flex items-center gap-1" data-testid="login-back"><ArrowLeft size={12} /> Back</button>
                    <div className="flex gap-4">
                      <button type="button" onClick={sendLoginOtp} className="text-[#7E1E3F] hover:underline" data-testid="login-use-otp">Use OTP instead</button>
                      <Link to="/forgot" className="text-[#7E1E3F] hover:underline" data-testid="auth-forgot">Forgot password?</Link>
                    </div>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ---------- SIGNUP FORM ---------- */}
            {isSignup && step === "identify" && (
              <motion.div key="signup" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-serif-lux text-3xl text-[#2E1A22]">Create Account</h2>
                <p className="text-sm text-[#857078] mt-1.5">Join Savyron — faster checkout, order tracking, wishlists</p>
                <form onSubmit={signup} className="mt-7 space-y-4">
                  <Field label="Full Name" testid="signup-name" valid={form.name.trim().length >= 2}
                    error={show("name") && form.name && form.name.trim().length < 2 ? "Please enter your full name" : ""}>
                    <input className={`${input} pr-10`} placeholder="Rahul Kumar" value={form.name}
                      onChange={(e) => set("name", e.target.value)} onBlur={() => blur("name")} data-testid="auth-name" />
                  </Field>
                  <Field label="Mobile Number" testid="signup-phone" valid={PHONE_RE.test(form.phone)}
                    error={show("phone") && form.phone && !PHONE_RE.test(form.phone) ? "Please enter a valid 10-digit mobile number" : ""}>
                    <div className="flex">
                      <span className="bg-[#FBF7F1] border border-r-0 border-[#E5E7EB] rounded-l-xl px-3.5 py-3.5 text-sm text-[#857078] font-medium">+91</span>
                      <input className={`${input} rounded-l-none pr-10`} inputMode="numeric" maxLength={10} placeholder="98765 43210"
                        value={form.phone} onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))} onBlur={() => blur("phone")} data-testid="signup-phone-input" />
                    </div>
                  </Field>
                  <Field label="Email Address" testid="signup-email" valid={EMAIL_RE.test(form.email)}
                    error={show("email") && form.email && !EMAIL_RE.test(form.email) ? "Please enter a valid email address" : ""}>
                    <input type="email" className={`${input} pr-10`} placeholder="name@gmail.com — Gmail, Yahoo, Outlook, any provider" value={form.email}
                      onChange={(e) => set("email", e.target.value)} onBlur={() => blur("email")} data-testid="auth-email" />
                  </Field>
                  <Field label="Password" testid="signup-password" valid={pwOk(form.password)}
                    error={show("password") && form.password && !pwOk(form.password) ? "Password must meet the minimum security requirements (8+ chars, letter + number)" : ""}>
                    <input type={showPw ? "text" : "password"} className={`${input} pr-10`} placeholder="Min 8 characters, letter + number"
                      value={form.password} onChange={(e) => set("password", e.target.value)} onBlur={() => blur("password")} data-testid="signup-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#857078]" data-testid="toggle-password">
                      {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </Field>
                  <Field label="Confirm Password" testid="signup-confirm" valid={!!form.confirm && form.confirm === form.password}
                    error={show("confirm") && form.confirm && form.confirm !== form.password ? "Passwords do not match" : ""}>
                    <input type="password" className={`${input} pr-10`} placeholder="Repeat your password"
                      value={form.confirm} onChange={(e) => set("confirm", e.target.value)} onBlur={() => blur("confirm")} data-testid="signup-confirm-input" />
                  </Field>
                  {error && <p className="text-sm text-[#E63946]" data-testid="auth-error">❌ {error}</p>}
                  <button disabled={loading}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
                    data-testid="signup-submit">
                    {loading ? "Sending code…" : "Create Account"}
                  </button>
                </form>
              </motion.div>
            )}

            {/* ---------- FORGOT: EMAIL ---------- */}
            {isForgot && step === "identify" && (
              <motion.div key="forgot" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-serif-lux text-3xl text-[#2E1A22]">Forgot Password</h2>
                <p className="text-sm text-[#857078] mt-1.5">Enter your registered email — we'll send a verification code.</p>
                <form onSubmit={forgotSend} className="mt-7 space-y-5">
                  <Field label="Email Address" testid="forgot-email" valid={EMAIL_RE.test(form.email)}
                    error={show("email") && form.email && !EMAIL_RE.test(form.email) ? "Please enter a valid email address" : ""}>
                    <input type="email" className={`${input} pr-10`} placeholder="name@gmail.com" value={form.email}
                      onChange={(e) => set("email", e.target.value)} onBlur={() => blur("email")} data-testid="forgot-email-input" />
                  </Field>
                  {error && <p className="text-sm text-[#E63946]" data-testid="auth-error">❌ {error}</p>}
                  <button disabled={loading}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
                    data-testid="forgot-submit">
                    {loading ? "Sending…" : "Send Verification Code"}
                  </button>
                  <Link to="/login" className="block text-center text-xs text-[#7E1E3F] hover:underline" data-testid="forgot-back-login">Back to Sign In</Link>
                </form>
              </motion.div>
            )}

            {/* ---------- FORGOT: NEW PASSWORD ---------- */}
            {isForgot && step === "newpass" && (
              <motion.div key="newpass" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35, ease: EASE }}>
                <h2 className="font-serif-lux text-3xl text-[#2E1A22]">Set New Password</h2>
                <p className="text-sm text-[#857078] mt-1.5">Choose a fresh password for {form.email}.</p>
                <form onSubmit={resetPassword} className="mt-7 space-y-4">
                  <Field label="New Password" testid="reset-password" valid={pwOk(form.password)}
                    error={show("password") && form.password && !pwOk(form.password) ? "Password must meet the minimum security requirements" : ""}>
                    <input type="password" className={`${input} pr-10`} placeholder="Min 8 characters, letter + number"
                      value={form.password} onChange={(e) => set("password", e.target.value)} onBlur={() => blur("password")} data-testid="reset-password-input" />
                  </Field>
                  <Field label="Confirm Password" testid="reset-confirm" valid={!!form.confirm && form.confirm === form.password}
                    error={show("confirm") && form.confirm && form.confirm !== form.password ? "Passwords do not match" : ""}>
                    <input type="password" className={`${input} pr-10`} placeholder="Repeat new password"
                      value={form.confirm} onChange={(e) => set("confirm", e.target.value)} onBlur={() => blur("confirm")} data-testid="reset-confirm-input" />
                  </Field>
                  {error && <p className="text-sm text-[#E63946]" data-testid="auth-error">❌ {error}</p>}
                  <button disabled={loading}
                    className="w-full bg-[#7E1E3F] text-white font-semibold text-sm tracking-wider uppercase rounded-xl py-4 hover:bg-[#5C1230] transition-colors disabled:opacity-60"
                    data-testid="reset-submit">
                    {loading ? "Updating…" : "Update Password"}
                  </button>
                </form>
              </motion.div>
            )}

            {/* ---------- OTP STEP (shared) ---------- */}
            {step === "otp" && (
              <OtpStep key="otp" email={form.email} phone={form.phone} channel={channel}
                purpose={isSignup ? "signup" : isForgot ? "reset" : "login"}
                onVerified={onOtpVerified}
                onBack={() => setStep(isSignup || isForgot || channel === "sms" ? "identify" : "password")}
                onSwitchToEmail={isSignup && channel === "sms" ? async () => {
                  try {
                    await api.post("/auth/otp/send", { email: form.email, purpose: "signup" });
                    setChannel("email");
                    toast.success("Verification code sent to your email");
                  } catch (err) { toast.error(formatApiError(err)); }
                } : null} />
            )}
          </AnimatePresence>

          {step !== "otp" && step !== "newpass" && (
            <>
              <div className="flex items-center gap-3 my-6">
                <span className="flex-1 h-px bg-[#E5E7EB]" />
                <span className="text-[11px] font-mono-lux tracking-[0.2em] uppercase text-[#857078]">OR</span>
                <span className="flex-1 h-px bg-[#E5E7EB]" />
              </div>
              <GoogleButton onDone={done} />
            </>
          )}

          <p className="text-[11px] text-[#857078] text-center mt-6 leading-relaxed">
            By continuing, you agree to our <Link to="/terms" className="text-[#7E1E3F] underline" data-testid="auth-terms">Terms of Service</Link> and <Link to="/privacy" className="text-[#7E1E3F] underline" data-testid="auth-privacy">Privacy Policy</Link>.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
